import { parse } from 'cookie'

import { FEATURES, MODULES } from '../constants/template-info'

import eraseCookie from './eraseCookie'
import isBrowser from './isBrowser'
import { captureException } from './sentryUtil'
import setCookie from './setCookie'

let cachedCookieString: string | undefined
let cachedParsedCookies: Record<string, string> = {}

const getParsedCookies = (): Record<string, string> => {
  if (!isBrowser()) return {}

  const cookieString = document.cookie
  if (cookieString !== cachedCookieString) {
    // Telemetry reads many properties consecutively. Keying by the browser-visible string avoids
    // reparsing it for every property while still invalidating immediately after any cookie change.
    const parsedCookies = parse(cookieString)
    cachedCookieString = cookieString
    cachedParsedCookies = parsedCookies
  }
  return cachedParsedCookies
}

// /**
//  * @deprecated Use EnhancedCookie utility instead:
//  * ```typescript
//  * import { getCookie } from '@src/utils/enhancedCookie'
//  * const cookie = getCookie(key, { isGlobal: true })
//  * ```
//  */
const getCookie = (cname: string): string | undefined => {
  if (!isBrowser()) return undefined

  try {
    const parsedCookies = getParsedCookies()
    return parsedCookies[cname]
  } catch (error) {
    captureException(error, {
      module: MODULES.UNKNOWN,
      feature: FEATURES.GET_COOKIE,
    })
  }

  // Fallback to original implementation

  // @i18n-ignore
  const name = `${cname}=`
  try {
    const decodedCookie = document.cookie
    const cookiesArray = decodedCookie.split(';')
    for (let cookie of cookiesArray) {
      cookie = cookie.trimStart()
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length, cookie.length)
      }
    }
  } catch (error) {
    captureException(error, {
      module: MODULES.UNKNOWN,
      feature: FEATURES.GET_COOKIE,
    })
  }

  return undefined
}

// /**
//  * @deprecated Use EnhancedCookie utility instead:
//  * ```typescript
//  * import { getCookie } from '@src/utils/enhancedCookie'
//  * ```
//  */
export const areCookiesEnabled = () => {
  let isEnabled = false
  const testCookieName = 'testName'
  const testCookieValue = 'testValue'
  setCookie(testCookieName, testCookieValue, 1)
  if (getCookie(testCookieName) === testCookieValue) {
    isEnabled = true
    eraseCookie(testCookieName)
  } else {
    captureException('Cookie storage is not enabled', {
      module: MODULES.COOKIE_STORAGE,
      feature: FEATURES.GET_COOKIE,
    })
  }
  return isEnabled
}

export default getCookie
